'use client';

import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog';
import { cn } from '@/lib/cn';
import * as Tooltip from '@radix-ui/react-tooltip';
import { InfoIcon } from 'lucide-react';
import type { InputProps } from './Input';

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md has-data-[slot=command-input-label]:overflow-visible',
        className,
      )}
      {...props}
    />
  );
}

function CommandDialog({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
}) {
  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        className={cn('overflow-hidden p-0', className)}
        showCloseButton={showCloseButton}
      >
        <Command className="**:[[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group]]:px-2 **:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export interface CommandInputProps extends Omit<
  InputProps,
  'value' | 'onValueChange'
> {
  value?: string;
  onValueChange?: (value: string) => void;
}

const CommandInput = React.forwardRef<HTMLInputElement, CommandInputProps>(
  (
    {
      className,
      label,
      error,
      touched,
      id,
      value,
      defaultValue,
      info,
      onValueChange,
      ...props
    },
    ref,
  ) => {
    return (
      <div className="group relative w-full">
        <div className="relative">
          <CommandPrimitive.Input
            data-slot="command-input"
            className={cn(
              'border-input ring-offset-background focus-visible:border-secondary peer flex w-full rounded-[0.625rem] border bg-transparent px-4 py-4 text-lg file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-transparent autofill:shadow-[0_0_0_62.5rem_var(--background)_inset] focus-visible:border-[0.09375rem] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&-webkit-autofill]:transition-colors [&-webkit-autofill]:duration-[5000s] [&-webkit-autofill]:[-webkit-text-fill-color:var(--foreground)]',
              touched && error
                ? 'border-destructive focus-visible:border-destructive'
                : '',
              className,
            )}
            ref={ref}
            placeholder=" "
            id={id}
            value={value}
            defaultValue={
              typeof defaultValue === 'string'
                ? defaultValue
                : defaultValue !== undefined
                  ? String(defaultValue)
                  : undefined
            }
            onValueChange={onValueChange}
            {...props}
          />
          {label && (
            <div
              data-slot="command-input-label"
              className={cn(
                'pointer-events-none absolute left-4 flex h-full items-center transition-all duration-200 ease-out',
                'peer-focus:bg-background peer-focus:-top-3 peer-focus:h-auto peer-focus:px-1 peer-focus:text-xs',
                (touched && error) || value || defaultValue
                  ? 'bg-background -top-3 h-auto px-1 text-xs'
                  : 'top-0',
              )}
            >
              <label
                htmlFor={id}
                className={cn(
                  'text-muted-foreground font-normal transition-colors duration-200',
                  touched && error
                    ? 'text-destructive group-focus-within:text-destructive'
                    : 'group-focus-within:text-secondary',
                )}
              >
                {label}
              </label>
              {info && (
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-secondary pointer-events-auto ml-1.5 focus:outline-none"
                      >
                        <InfoIcon className="size-3.5" />
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        className="bg-secondary text-primary-foreground animate-in fade-in zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-w-xs rounded-lg px-3 py-1.5 text-sm shadow-md"
                        sideOffset={5}
                      >
                        {info}
                        <Tooltip.Arrow className="fill-secondary" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              )}
            </div>
          )}
          {touched && error && (
            <div
              className={cn(
                'text-destructive pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-sm font-light',
              )}
            >
              {error}
            </div>
          )}
        </div>
      </div>
    );
  },
);
CommandInput.displayName = 'CommandInput';

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        'max-h-80 scroll-py-1 overflow-x-hidden overflow-y-auto',
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'text-foreground **:[[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium',
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn('bg-border -mx-1 h-px', className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        'text-muted-foreground ml-auto text-xs tracking-widest',
        className,
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
